<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { INITIAL_TEAMS, INITIAL_PLAYERS, INITIAL_EVENTS } from '@/services/mockData';
import AppBadge from '@/components/ui/AppBadge.vue';
import { NCard, NButton, NTag } from 'naive-ui';
import {
  Trophy,
  Users2,
  Calendar,
  Flame,
  ChevronRight,
  Clock,
  MapPin,
  CheckCircle2,
  Plus,
  ArrowUpRight
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';

const { t, locale } = useI18n();
const router = useRouter();
const { activeTenant } = useTenant();

// Tenant scoped data
const tenantTeams = computed(() => INITIAL_TEAMS.filter(t => t.tenantId === activeTenant.value.id));
const tenantPlayers = computed(() => INITIAL_PLAYERS.filter(p => p.tenantId === activeTenant.value.id));
const tenantEvents = computed(() => INITIAL_EVENTS.filter(e => e.tenantId === activeTenant.value.id));

const totalActivePlayers = computed(() => tenantPlayers.value.filter(p => p.status === 'ACTIVE').length);

const upcomingEvents = computed(() => {
  return [...tenantEvents.value].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
});

const formatDate = (isoString: string) => {
  const date = new Date(isoString);
  const localeMap: Record<string, string> = {
    'ja': 'ja-JP',
    'zh-TW': 'zh-TW',
    'ko': 'ko-KR',
    'en': 'en-US',
  };
  return date.toLocaleDateString(localeMap[locale.value] || 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};
</script>

<template>
  <div class="space-y-8 animate-in fade-in duration-200">
    <!-- Header banner -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-2xl sm:text-3xl font-extrabold text-slate-100 tracking-tight">
            {{ t('coachDashboard.title') }}
          </h1>
          <AppBadge variant="accent" pill>{{ activeTenant.type }}</AppBadge>
        </div>
        <p class="mt-1 text-xs sm:text-sm text-slate-400">
          {{ t('coachDashboard.subtitle', { name: activeTenant.name }) }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <NButton
          type="primary"
          @click="router.push('/dashboard/teams')"
          class="font-bold text-xs"
        >
          <template #icon>
            <Plus class="w-4 h-4" />
          </template>
          {{ t('coachDashboard.manageTeams') }}
        </NButton>
      </div>
    </div>

    <!-- Metric Stat Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Teams -->
      <NCard hoverable class="bg-slate-900 border-slate-800/80 rounded-2xl shadow-lg">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('coachDashboard.activeTeams') }}</span>
          <div class="w-9 h-9 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
            <Trophy class="w-4 h-4" />
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <span class="text-3xl font-extrabold text-slate-100">{{ tenantTeams.length }}</span>
          <NTag type="success" size="small" round :bordered="false">{{ t('coachDashboard.inSeason') }}</NTag>
        </div>
      </NCard>

      <!-- Active Roster -->
      <NCard hoverable class="bg-slate-900 border-slate-800/80 rounded-2xl shadow-lg">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('coachDashboard.activeRoster') }}</span>
          <div class="w-9 h-9 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
            <Users2 class="w-4 h-4" />
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <span class="text-3xl font-extrabold text-slate-100">{{ totalActivePlayers }}</span>
          <span class="text-xs text-slate-400">/ {{ tenantPlayers.length }} {{ t('coachDashboard.total') }}</span>
        </div>
      </NCard>

      <!-- Upcoming Events -->
      <NCard hoverable class="bg-slate-900 border-slate-800/80 rounded-2xl shadow-lg">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('coachDashboard.scheduledEvents') }}</span>
          <div class="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
            <Calendar class="w-4 h-4" />
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <span class="text-3xl font-extrabold text-slate-100">{{ tenantEvents.length }}</span>
          <NTag type="info" size="small" round :bordered="false">{{ t('coachDashboard.next7Days') }}</NTag>
        </div>
      </NCard>

      <!-- Win Rate -->
      <NCard hoverable class="bg-slate-900 border-slate-800/80 rounded-2xl shadow-lg">
        <div class="flex items-center justify-between">
          <span class="text-xs font-semibold text-slate-400 uppercase tracking-wider">{{ t('coachDashboard.winRate') }}</span>
          <div class="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
            <Flame class="w-4 h-4" />
          </div>
        </div>
        <div class="mt-3 flex items-baseline gap-2">
          <span class="text-3xl font-extrabold text-slate-100">.824</span>
          <NTag type="success" size="small" round :bordered="false">14 - 3 W/L</NTag>
        </div>
      </NCard>
    </div>

    <!-- Main Grid: Upcoming Schedule & Active Teams -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <!-- Upcoming Schedule (2 Cols) -->
      <div class="lg:col-span-2 space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-bold text-slate-100 flex items-center gap-2">
            <Calendar class="w-4 h-4 text-sky-400" />
            {{ t('coachDashboard.upcomingGames') }}
          </h2>
          <router-link to="/dashboard/events" class="text-xs font-semibold text-sky-400 hover:text-sky-300 flex items-center gap-1">
            {{ t('coachDashboard.viewAllSchedule') }} <ChevronRight class="w-3.5 h-3.5" />
          </router-link>
        </div>

        <div class="space-y-3">
          <div
            v-for="event in upcomingEvents"
            :key="event.id"
            class="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5 hover:border-slate-700 transition flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div class="flex items-start gap-3.5">
              <div
                :class="[
                  'w-12 h-12 rounded-xl flex flex-col items-center justify-center font-bold text-xs shrink-0',
                  event.eventType === 'GAME' ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400' : 'bg-sky-500/10 border border-sky-500/20 text-sky-400'
                ]"
              >
                <span>{{ event.eventType === 'GAME' ? t('coachDashboard.game') : t('coachDashboard.practice') }}</span>
              </div>

              <div>
                <div class="flex items-center gap-2">
                  <h3 class="font-bold text-slate-100 text-base">{{ event.title }}</h3>
                  <AppBadge v-if="event.homeAway" :variant="event.homeAway === 'HOME' ? 'success' : 'slate'">
                    {{ event.homeAway === 'HOME' ? t('common.home') : t('common.away') }}
                  </AppBadge>
                </div>

                <div class="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
                  <span class="flex items-center gap-1.5 text-slate-300">
                    <Clock class="w-3.5 h-3.5 text-slate-400" />
                    {{ formatDate(event.startTime) }}
                  </span>
                  <span class="flex items-center gap-1.5">
                    <MapPin class="w-3.5 h-3.5 text-slate-500" />
                    {{ event.location }}
                  </span>
                </div>

                <p v-if="event.opponent" class="mt-1 text-xs text-slate-400">
                  VS: <strong class="text-slate-200">{{ event.opponent }}</strong>
                </p>
              </div>
            </div>

            <!-- Attendance quick status -->
            <div v-if="event.attendanceSummary" class="flex sm:flex-col items-end justify-between sm:justify-center border-t sm:border-t-0 border-slate-800 pt-2 sm:pt-0">
              <div class="flex items-center gap-1 text-xs font-semibold text-emerald-400">
                <CheckCircle2 class="w-3.5 h-3.5" />
                <span>{{ event.attendanceSummary.attending }} {{ t('coachDashboard.confirmed') }}</span>
              </div>
              <span class="text-[10px] text-slate-500">{{ event.attendanceSummary.declined }} {{ t('coachDashboard.out') }} / {{ event.attendanceSummary.pending }} {{ t('coachDashboard.pending') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Teams Quick List (1 Col) -->
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-lg font-bold text-slate-100 flex items-center gap-2">
            <Trophy class="w-4 h-4 text-emerald-400" />
            {{ t('coachDashboard.clubTeams') }}
          </h2>
          <router-link to="/dashboard/teams" class="text-xs font-semibold text-sky-400 hover:text-sky-300">
            {{ t('coachDashboard.manage') }}
          </router-link>
        </div>

        <div class="space-y-3">
          <div
            v-for="team in tenantTeams"
            :key="team.id"
            @click="router.push(`/dashboard/teams`)"
            class="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-slate-700 hover:bg-slate-800/40 transition cursor-pointer group"
          >
            <div class="flex items-start justify-between">
              <div>
                <h3 class="font-bold text-slate-100 text-sm group-hover:text-sky-400 transition">{{ team.name }}</h3>
                <p class="text-xs text-slate-400 mt-0.5">{{ team.division }}</p>
              </div>
              <ArrowUpRight class="w-4 h-4 text-slate-500 group-hover:text-sky-400 transition" />
            </div>

            <div class="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs">
              <span class="text-slate-400">{{ t('coachDashboard.coach') }}: <span class="text-slate-200 font-medium">{{ team.headCoach }}</span></span>
              <span class="font-mono font-bold text-emerald-400">{{ team.wins }}W - {{ team.losses }}L</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>